import {
	convertToCss,
	fontFamilyDefinition,
	fontSizeDefinition,
	supportedFontFamilies,
	supportedFontSizes
} from "client/Misc/fontTools";
import {
	FontFamily,
	FontSize,
	FontStyleModel
} from "client/Api/common";
import { localize } from "client_legacy/ndaw/culture";
import {
	CSSProperties,
	PropsWithChildren,
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { ColorPicker } from "../ColorPicker/ColorPicker";
import { Icon } from "../Icon/Icon";
import { RichTextEditorToolbar } from "../RichTextEditor/Toolbar/RichTextEditorToolbar";
import { Select, SelectItem } from "../Select/Select";
import styles from "./FontStylePicker.module.less";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

export type FontStylePickerProps = {
	value: FontStyleModel;
	onChange(fontStyle: FontStyleModel): void;
};

export const FontStylePicker = (props: PropsWithChildren<FontStylePickerProps>): ReactElement => {
	const {
		value,
		onChange,
		children
	} = props;

	const fontFamilyChangeHandler = useCallback(
		(newValue: FontFamily | null): void => {
			onChange({
				...value,
				family: newValue
			});
		},
		[value, onChange]
	);

	const fontFamilyOptions = useMemo(
		(): SelectItem<FontFamily | null>[] => {
			return [
				{
					value: null,
					text: `(${localize("_DefaultFont")})`,
					valueTrackingId: "default"
				},
				...supportedFontFamilies.map(
					family => ({
						value: family,
						text: fontFamilyDefinition[family].title
					})
				)
			];
		},
		[]
	);

	const fontSizeChangeHandler = useCallback(
		(newValue: FontSize | null): void => {
			onChange({
				...value,
				size: newValue
			});
		},
		[value, onChange]
	);

	const fontSizeOptions = useMemo(
		(): SelectItem<FontSize | null>[] => {
			return [
				{
					value: null,
					text: `(${localize("_DefaultSize")})`,
					valueTrackingId: "default"
				},
				...supportedFontSizes.map(
					size => ({
						value: size,
						text: fontSizeDefinition[size].title
					})
				)
			];
		},
		[]
	);

	const toggleBoldHandler = useCallback(
		(): void => {
			onChange(
				{
					...value,
					bold: !value.bold
				}
			);
		},
		[value, onChange]
	);

	const toggleItalicHandler = useCallback(
		(): void => {
			onChange(
				{
					...value,
					italic: !value.italic
				}
			);
		},
		[value, onChange]
	);

	const toggleUnderlineHandler = useCallback(
		(): void => {
			onChange(
				{
					...value,
					underline: !value.underline
				}
			);
		},
		[value, onChange]
	);

	const colorChangeHandler = useCallback(
		(newValue: string): void => {
			onChange({
				...value,
				color: newValue
			});
		},
		[value, onChange]
	);

	const resultStyles = useMemo(
		(): CSSProperties => {
			return convertToCss(value);
		},
		[value]
	);

	return (
		<div>
			<div className={styles.toolbar}>
				<RichTextEditorToolbar.ButtonGroup doesContainSingleInput>
					<Select
						value={value.family}
						onChange={fontFamilyChangeHandler}
						options={fontFamilyOptions}
						trackingId="font-family"
						menuTitle={localize("_FontFamily")}
						tooltip={localize("_FontFamily")}
					/>
				</RichTextEditorToolbar.ButtonGroup>
				<NoUniqueInputIdContext>
					<RichTextEditorToolbar.ButtonGroup doesContainSingleInput>
						<Select
							value={value.size}
							onChange={fontSizeChangeHandler}
							options={fontSizeOptions}
							trackingId="font-size"
							menuTitle={localize("_FontSize")}
							tooltip={localize("_FontSize")}
						/>
					</RichTextEditorToolbar.ButtonGroup>
					<RichTextEditorToolbar.ButtonGroup>
						<RichTextEditorToolbar.Button
							attachedOn="right"
							onClick={toggleBoldHandler}
							isActive={value.bold}
							trackingId="bold"
							tooltip={localize("_Bold")}
						>
							<Icon value="bold" />
						</RichTextEditorToolbar.Button>
						<RichTextEditorToolbar.Button
							attachedOn={["left", "right"]}
							onClick={toggleItalicHandler}
							isActive={value.italic}
							trackingId="italic"
							tooltip={localize("_Italic")}
						>
							<Icon value="italic" />
						</RichTextEditorToolbar.Button>
						<RichTextEditorToolbar.Button
							attachedOn="left"
							onClick={toggleUnderlineHandler}
							isActive={value.underline}
							trackingId="underline"
							tooltip={localize("_Underline")}
						>
							<Icon value="underline" />
						</RichTextEditorToolbar.Button>
					</RichTextEditorToolbar.ButtonGroup>
					<RichTextEditorToolbar.ButtonGroup>
						<ColorPicker
							value={value.color}
							onChange={colorChangeHandler}
							trackingId="color-picker"
							menuTitle={localize("_FontColor")}
							tooltip={localize("_FontColor")}
						/>
					</RichTextEditorToolbar.ButtonGroup>
				</NoUniqueInputIdContext>
			</div>
			{children && (
				<div style={resultStyles}>
					{children}
				</div>
			)}
		</div>
	);
};
FontStylePicker.displayName = "FontStylePicker";